import { useMutation } from "@tanstack/react-query";
import { useSession } from "@clerk/clerk-react";
import { useEffect, useRef } from "react";
import { usePostHog } from "posthog-js/react";

async function sendLoginNotification(): Promise<{ success: boolean }> {
  const response = await fetch("/api/notify-login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to send login notification");
  }

  return response.json();
}

export function useLoginNotification() {
  return useMutation({
    mutationFn: sendLoginNotification,
    // Don't show errors to user - this is a background notification
    onError: (error) => {
      console.error("Failed to send login notification:", error);
    },
  });
}

// Hook to automatically detect session changes and send notifications
export function useSessionLoginDetection() {
  const { session, isLoaded } = useSession();
  const loginNotification = useLoginNotification();
  const lastSessionId = useRef<string | null>(null);
  const posthog = usePostHog();

  useEffect(() => {
    if (!isLoaded) return;

    const currentSessionId = session?.id || null;

    // If we have a new session (and it's not the initial load)
    if (
      currentSessionId &&
      currentSessionId !== lastSessionId.current &&
      lastSessionId.current !== undefined
    ) {
      loginNotification.mutate();
    }

    lastSessionId.current = currentSessionId;

    const currentUserId = session?.user?.id || null;
    if (currentUserId && posthog) {
      const user = session?.user;
      const email = user?.emailAddresses?.[0]?.emailAddress;

      if (email) {
        posthog.setPersonPropertiesForFlags({ email });
      }

      posthog.identify(currentUserId, {
        firstName: user?.firstName,
        fullName: user?.fullName,
        lastName: user?.lastName,
        email,
      });
    }
  }, [session?.id, isLoaded, loginNotification, posthog]);
}
